import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { UserDto } from "@evolys/types";
import {
  ApiClient,
  AuthApi,
  STAFF_SESSION_EXPIRED_EVENT,
  getRuntimeConfig,
} from "@evolys/api-client";

interface AuthContextValue {
  user: UserDto | null;
  roles: string[];
  permissions: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const configuredTimeoutMinutes = Number.parseInt(
  getRuntimeConfig("SESSION_TIMEOUT_MINUTES", undefined, "15"),
  10,
);
const INACTIVITY_TIMEOUT_MS =
  (Number.isFinite(configuredTimeoutMinutes)
    ? configuredTimeoutMinutes
    : 15) *
  60 *
  1000;
const WARNING_BEFORE_MS = Math.min(
  3 * 60 * 1000,
  INACTIVITY_TIMEOUT_MS / 2,
);

interface AuthProviderProps {
  children: ReactNode;
  apiBaseUrl: string;
  onSessionExpired?: () => void;
  onInactivityWarning?: () => void;
}

export function AuthProvider({
  children,
  apiBaseUrl,
  onSessionExpired,
  onInactivityWarning,
}: AuthProviderProps) {
  const [user, setUser] = useState<UserDto | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const authApi = useRef(new AuthApi(new ApiClient(apiBaseUrl))).current;
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
  }, []);

  const handleSessionExpired = useCallback(() => {
    setUser(null);
    setRoles([]);
    setPermissions([]);
    clearTimers();
    onSessionExpired?.();
  }, [clearTimers, onSessionExpired]);

  const resetInactivityTimer = useCallback(() => {
    clearTimers();
    if (!user) return;

    warningTimer.current = setTimeout(() => {
      onInactivityWarning?.();
    }, INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_MS);

    inactivityTimer.current = setTimeout(async () => {
      try {
        await authApi.logout();
      } catch {
        // ignore — session may already be expired
      }
      handleSessionExpired();
    }, INACTIVITY_TIMEOUT_MS);
  }, [user, clearTimers, authApi, handleSessionExpired, onInactivityWarning]);

  useEffect(() => {
    window.addEventListener(
      STAFF_SESSION_EXPIRED_EVENT,
      handleSessionExpired,
    );
    return () => {
      window.removeEventListener(
        STAFF_SESSION_EXPIRED_EVENT,
        handleSessionExpired,
      );
    };
  }, [handleSessionExpired]);

  // Track user activity for inactivity timeout
  useEffect(() => {
    if (!user) return;

    const events = ["mousedown", "keydown", "scroll", "touchstart"] as const;
    const handler = () => resetInactivityTimer();

    for (const event of events) {
      window.addEventListener(event, handler, { passive: true });
    }
    resetInactivityTimer();

    return () => {
      for (const event of events) {
        window.removeEventListener(event, handler);
      }
      clearTimers();
    };
  }, [user, resetInactivityTimer, clearTimers]);

  // Check session on mount
  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      try {
        const result = await authApi.me();
        if (!cancelled) {
          setUser(result.user);
          setRoles(result.roles);
          setPermissions(result.permissions);
        }
      } catch {
        // Not authenticated — that's fine
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    checkSession();
    return () => {
      cancelled = true;
    };
  }, [authApi]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await authApi.login({ email, password });
      setUser(result.user);
      setRoles(result.roles);
      setPermissions(result.permissions);
    },
    [authApi],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore
    }
    setUser(null);
    setRoles([]);
    setPermissions([]);
    clearTimers();
  }, [authApi, clearTimers]);

  const value: AuthContextValue = {
    user,
    roles,
    permissions,
    isAuthenticated: user !== null,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
